import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import PdfHeader from "../components/pdfHeader";
import { formatMoneyPunto } from "utils/formatters";
import { formatDate } from "utils/formatters";
import { Money2Text } from "utils/formatters";
import { nombreCompraDetalle, unidadCompraDetalle } from "utils/presentacionCompra";

const tipoDocumentos = [
  { id: 80, label: "CUIT" },
  { id: 86, label: "CUIL" },
  { id: 96, label: "DNI" },
]

const OP = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;

  const [data, setData] = useState(null);
  const [getProvision, { loading }] = useLazyQuery(
    gql`
      query getProvision($id: ID) {
        provisiones(filter: { id: $id }) {
          id
          pedidoCompra {
            id
            numeroExpediente
            tipoCompra
            domicilioEntrega
            fechaEntrega
            createdUser {
              nombre
              apellido
              telefono
              email
            }
          }
          proveedor {
            id
            tipoProveedor
            razonSocial
            nombre
            apellido
            tipoDocumento
            documento
            user {
              email
            }
          }
          detalles {
            id
            materiaPrima {
              nombre
              nombreCompra
              tipoUnidad {
                nombre
              }
            }
            pedidoCompraDetalle {
              numero
              nombreCompra
              unidadNombre
              unidadAbreviatura
              requierePack
              unidadPack
              nombreServicio
              cotizaciones {
                plazoEntrega
              }
            }
            precioUnitario
            cantidad
            subtotal
          }
          total
          anulada
          motivoAnulacion
          fechaVencimiento
          motivoProrroga
          createdAt
          estado
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (!data?.provisiones[0]) return;
        setData(data.provisiones[0]);
      },
    }
  );

  useEffect(() => {
    if (id) {
      getProvision({ variables: { id } });
    }
  }, [id]);

  const generateWaterMark = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 400;
    canvas.height = 400;
    ctx.font = "80px Arial";
    ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
    ctx.textAlign = "center";
    //rotate 45 degrees
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((-45 * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);


    ctx.fillText("ANULADA", canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL();
  };

  const handleGeneratePdf = async () => {
    const document = documentsRef.current.childNodes[0];
    const doc = new jsPDF({
      format: "a4",
      unit: "px",
      orientation: "portrait",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    await doc.html(document, {
      margin: [40, 0, 40, 0],
      callback(doc) {
        doc.save(`exd-OP-${data.id}.pdf`);
      },
    });
  };

  if (!data) return (
    <EmpleadoLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={3}>
        {loading ? (<>
          <CircularProgress size={40} color="primary" />
          <SoftTypography variant="h5">Cargando</SoftTypography>
        </>) : (<>
          <Icon fontSize="large">search_off</Icon>
          <SoftTypography variant="h5">No se encontró la orden</SoftTypography>
        </>)}
      </SoftBox>
    </EmpleadoLayout>
  );

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
        gap={4}
      >
        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "794px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            {/* ACA VA TODO EL CODIGO */}
            <PdfHeader />

            {data.anulada && (
              <img
                src={generateWaterMark()}
                alt="anulada"
                style={{
                  position: "absolute",
                  top: "25%",
                  left: "25%",
                  zIndex: 1000,
                }}
              />
            )}

            <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={2} fontSize="12px">
              <Grid
                container
                border="1pt solid black"
              >
                <Grid item xs={8} pl={1} fontWeight="bold">
                  <div>ORDEN DE PROVISIÓN N° {data.id}</div>
                  <div>PARA PROVEEDOR</div>
                  {data.anulada && (<>
                    <span style={{ color: "red" }}>ANULADA: </span><span style={{ fontWeight: "normal" }}>{data.motivoAnulacion}</span>
                  </>)}
                </Grid>
                <Grid item xs={4} textAlign="center" borderLeft="1pt solid black">
                  <SoftBox borderBottom="1pt solid black">Monto Total de la Orden</SoftBox>
                  <SoftBox fontWeight="bold">$ {formatMoneyPunto(data.total ?? 0)}</SoftBox>
                </Grid>
              </Grid>

              <Grid
                container
                border="1pt solid black"
                lineHeight={1.5}
                sx={{ '& > *:not(:nth-last-of-type(-n+2))': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
                  Fecha de Orden
                </Grid>
                <Grid item xs={3} px={1}>
                  {formatDate(data.createdAt, "dd/MM/yyyy")}
                </Grid>

                <Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Tipo de Contratación</Grid>
                <Grid item xs={3} px={1} borderRight="1pt solid black">
                  {data.pedidoCompra?.tipoCompra?.toUpperCase() ?? "-"}
                </Grid>

                <Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
                  Fecha de Entrega
                </Grid>
                <Grid item xs={3} px={1}>
                  {formatDate(data.pedidoCompra?.fechaEntrega, "dd/MM/yyyy")}
                </Grid>
                <Grid item xs={6} />

                <Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Domicilio de Entrega</Grid>
                <Grid item xs={9} px={1}>{data.pedidoCompra?.domicilioEntrega}</Grid>

                <Grid item xs={3} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Contacto</Grid>
                <Grid item xs={9} px={1}>comedorupro@uprosanluis.edu.ar</Grid>

              </Grid>

              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:nth-last-of-type(-n+1))': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>PROVEEDOR</Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>
                  {data.proveedor?.tipoProveedor === "Jurídica" ? "Razón Social" : "Apellido y Nombre"}
                </Grid>
                <Grid item xs={6} px={1}>
                  {data.proveedor?.razonSocial ?? `${data.proveedor?.apellido}, ${data.proveedor?.nombre}`}
                </Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>{
                  tipoDocumentos.find(e => e.id == data.proveedor?.tipoDocumento)?.label || 'Documento'}</Grid>
                <Grid item xs={6} px={1}>{data.proveedor?.documento ?? "-"}</Grid>

                <Grid item xs={6} borderRight="1pt solid black" backgroundColor="#f0f0f0" fontWeight="bold" px={1}>Email</Grid>
                <Grid item xs={6} px={1}>
                  {data.proveedor?.user?.email ?? "-"}
                </Grid>

                <Grid item xs={12} pl={1}>
                  Sírvase efectuar y/o proveer a: <span style={{ fontWeight: "bold" }}>FUNDACIÓN UPrO</span>
                </Grid>
              </Grid>

              <Grid
                container
                border="1pt solid black"
                sx={{ '& > *:not(:last-child)': { borderBottom: "1pt solid black" } }}
              >
                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1}>PRODUCTOS Y/O SERVICIOS A PROVEER</Grid>

                <Grid
                  item
                  container
                  xs={12}
                  backgroundColor="#f0f0f0"
                  fontWeight="bold"
                  sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                >
                  <Grid item xs={1} px={1} textAlign="center">Item</Grid>
                  <Grid item xs={4} px={1}>Descripción</Grid>
                  <Grid item xs={1.5} px={1} textAlign="center">Cantidad</Grid>
                  <Grid item xs={1.5} px={1} textAlign="center">Unidad</Grid>
                  <Grid item xs={2} px={1} textAlign="right">Precio U.</Grid>
                  <Grid item xs={2} px={1} textAlign="right">Precio Total</Grid>
                </Grid>
                {data?.detalles?.map((detalle, index) => (
                  <Grid
                    key={index}
                    lineHeight={1.5}
                    item
                    container
                    xs={12}
                    sx={{ '& > *:not(:last-child)': { borderRight: "1pt solid black" } }}
                  >
                    {console.log('detallex...', detalle)}
                    <Grid item xs={1} px={1} textAlign="center">
                      {detalle.pedidoCompraDetalle?.numero}
                    </Grid>
                    <Grid item xs={4} px={1}>
                      {nombreCompraDetalle(detalle.pedidoCompraDetalle, detalle.materiaPrima)}
                    </Grid>
                    <Grid item xs={1.5} px={1} textAlign="center">
                      {detalle.cantidad}
                    </Grid>
                    <Grid item xs={1.5} px={1} textAlign="center">
                      {unidadCompraDetalle(detalle.pedidoCompraDetalle, detalle.materiaPrima)}
                    </Grid>
                    <Grid item xs={2} px={1} textAlign="right">
                      $ {formatMoneyPunto(detalle?.precioUnitario ?? 0)}
                    </Grid>
                    <Grid item xs={2} px={1} textAlign="right">
                      $ {formatMoneyPunto((detalle.precioUnitario * detalle.cantidad) || 0)}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <SoftBox border="1px solid black">
                <SoftBox px={1} borderBottom="1px solid black" bgColor="#f0f0f0" display="flex" justifyContent="space-between" fontWeight="bold">
                  <div>TOTAL GENERAL DE LA PRESENTE ORDEN:</div> <div>$ {formatMoneyPunto(data.total ?? 0)}</div>
                </SoftBox>
                <SoftBox px={1}>{Money2Text(data.total ?? 0).toUpperCase()}</SoftBox>
              </SoftBox>
              {/* <SoftTypography variant="caption">
                Una vez realizada la entrega, solicitamos que envíe al siguiente correo: comedorupro@uprosanluis.edu.ar  la siguiente documentación:
              </SoftTypography> */}
              <ul style={{ lineHeight: 1.5, marginLeft: 12 }}>
                <li><strong>Sírvase efectuar y/o proveer a</strong>: FUNDACIÓN UPRO</li>
                <li><strong> DATOS DE FACTURACIÓN</strong>: Emitir Factura Original a nombre de FUNDACIÓN UPRO – CUIT 30-71886198-1 – Responsable Inscripto. La factura deberá detallar los ítems facturados, conforme a la correspondiente Orden de Provisión y/o Remito.</li>
                <li> Para consultas relacionadas con facturación y/o pagos pendientes, comunicarse al correo electrónico: <em><a href="#">comedorupro@uprosanluis.edu.ar</a></em>.</li>
              </ul>
              <SoftBox
                p={2}
                border="1px solid black"
                bgcolor="#ffffff"
                fontSize="12px"
              >
                <strong>Observaciones:</strong> La entrega se debe realizar en Comedor Upro, con domicilio en Calle Belgica S/N entre Madre Cabrini y Maipu, de la ciudad de Villa Mercedes (San Luis). <strong>La descarga de los materiales queda a cargo del Proveedor.</strong>
              </SoftBox>
              <SoftBox
                p={2}
                border="1px solid black"
                bgcolor="#ffffff"
                fontSize="12px"
              >
                <strong>Condiciones de entrega:</strong>
                <ul style={{ lineHeight: 1.5, marginLeft: 12 }}>
                  <li>{'Las entregas se ajustarán a lo solicitado por el área solicitante, de acuerdo con las Fichas de Entrega que serán notificadas oportunamente vía correo electrónico'}</li>
                  <li>{`Para consultas o coordinación de la entrega, los proveedores deberán comunicarse con el área solicitante al siguiente contacto Teléfono: ${data?.pedidoCompra?.createdUser?.telefono || "telefono responsable"} - ${data?.pedidoCompra?.createdUser?.nombre} ${data?.pedidoCompra?.createdUser?.apellido}`}</li>
                  <li>{'La recepción estará sujeta a control de cantidad, calidad, estado de conservación y fecha de vencimiento'}</li>
                </ul>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </div>
        <SoftBox
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end" id="button">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default OP;
